import { Drawer, theme } from 'antd';
import { ContactIcon, CreditCardIcon, EnableIcon, TeamIcon, UserIcon } from 'assets/images/svg';
import StatusIcon from 'assets/images/svg/StatusIcon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { selectUserTypes } from 'reduxStore/types/selectors';
import { ManagePersonDropdown } from 'zComponents/molecules';
import { Col, Collapse, Divider, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import {
	CredentialPanelContent,
	CredentialPanelExtra,
	DependantPanelContent,
	DependantPanelExtra,
	EditContactInput,
	EditDrawerHeader,
	EditSiteSelect,
	ErrorPersonContact,
	PanelHeader,
	PermissionsPanelContent,
	PermissionsPanelExtra,
	PersonDrawerSkeleton,
	TeamsPanelContent,
	TeamsPanelExtra
} from './components';
import { SitesIcon } from 'assets/images/svg/Menu';

const PersonDrawer = ({ personID, visible: visibleProp, setVisible: setVisibleProp }) => {
	// Internal state for visibility when not externally controlled
	const [internalVisible, setInternalVisible] = useState(false);

	// Use external state if provided, otherwise use internal state
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;
	// Theme tokens
	const {
		token: { paddingXXL = 48, borderRadiusLG, paddingMD, marginXS, paddingLG, padding }
	} = theme.useToken();
	const dispatch = useDispatch();

	// Refs
	const associatedPeopleRef = useRef(null);
	const associatedTeamsRef = useRef(null);
	const credentialsRef = useRef(null);
	const permissionsRef = useRef(null);

	// Redux state
	const person = useSelector((state) => selectPeopleByPersonID(state, personID));
	const userTypes = useSelector(selectUserTypes);

	// Local state
	const [activeKeys, setActiveKeys] = useState([
		'permissions',
		'teams',
		'associatedPeople',
		'credentials',
		'accessCards'
	]);
	const [isEditingDependants, setIsEditingDependants] = useState(false);
	const [isEditingCredentials, setIsEditingCredentials] = useState(false);
	const [isEditingTeams, setIsEditingTeams] = useState(false);
	const [isEditingPermissions, setIsEditingPermissions] = useState(false);
	const [errorContact, setErrorContact] = useState(false);
	const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
	const [isAssociatedPeopleModalOpen, setIsAssociatedPeopleModalOpen] = useState(false);
	const [isTeamsModalOpen, setIsTeamsModalOpen] = useState(false);
	const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
	// Load person contact info
	useEffect(() => {
		if (visible && personID) {
			dispatch(peopleActions.loadPersonContact({ personID }));
			setErrorContact(false);
		}
	}, [dispatch, personID, visible]);

	// Close drawer
	const handleOnClose = useCallback(() => {
		setVisible(false);
	}, [setVisible]);

	// Toggle panel
	const togglePanel = useCallback((key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	}, []);

	const handleCollapseChange = useCallback(
		(keys) => {
			if (isPermissionsModalOpen || isAssociatedPeopleModalOpen || isTeamsModalOpen || isCredentialsModalOpen) {
				return;
			}

			setActiveKeys(keys);

			// Reset editing state when panel closes
			if (!keys.includes('associatedPeople') && isEditingDependants) {
				setIsEditingDependants(false);
			}

			if (!keys.includes('teams') && isEditingTeams) {
				setIsEditingTeams(false);
			}

			if (!keys.includes('permissions') && isEditingPermissions) {
				setIsEditingPermissions(false);
			}
			if (!keys.includes('credentials') && isEditingCredentials) {
				setIsEditingCredentials(false);
			}
		},
		[
			isEditingDependants,
			isEditingTeams,
			isEditingPermissions,
			isPermissionsModalOpen,
			isAssociatedPeopleModalOpen,
			isTeamsModalOpen,
			isCredentialsModalOpen,
			isEditingCredentials
		]
	);

	// Memoize collapse items
	const items = useMemo(
		() => [
			{
				key: 'permissions',
				label: (
					<PanelHeader
						title='Access Permissions'
						iconLeft={<EnableIcon width={16} height={16} />}
						isExpanded={activeKeys.includes('permissions')}
						togglePanel={() => togglePanel('permissions')}
					/>
				),
				children: (
					<div ref={permissionsRef}>
						<PermissionsPanelContent personID={personID} isEditing={isEditingPermissions} />
					</div>
				),
				extra: (
					<PermissionsPanelExtra
						personID={personID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsPermissionsModalOpen}
					/>
				)
			},
			{
				key: 'divider-1',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'credentials',
				label: (
					<PanelHeader
						title='Credentials'
						iconLeft={<CreditCardIcon />}
						isExpanded={activeKeys.includes('credentials')}
						togglePanel={() => togglePanel('credentials')}
					/>
				),
				children: (
					<div ref={credentialsRef}>
						<CredentialPanelContent personID={personID} isEditing={isEditingCredentials} />
					</div>
				),
				extra: (
					<CredentialPanelExtra
						personID={personID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsCredentialsModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-2',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'teams',
				label: (
					<PanelHeader
						title='Teams'
						iconLeft={<TeamIcon />}
						isExpanded={activeKeys.includes('teams')}
						togglePanel={() => togglePanel('teams')}
					/>
				),
				children: (
					<div ref={associatedTeamsRef}>
						<TeamsPanelContent personID={personID} isEditing={isEditingTeams} />
					</div>
				),
				extra: (
					<TeamsPanelExtra
						personID={personID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsTeamsModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-3',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'associatedPeople',
				label: (
					<PanelHeader
						title='Associated People'
						iconLeft={<UserIcon />}
						isExpanded={activeKeys.includes('associatedPeople')}
						togglePanel={() => togglePanel('associatedPeople')}
					/>
				),
				children: (
					<div ref={associatedPeopleRef}>
						<DependantPanelContent personID={personID} isEditing={isEditingDependants} />
					</div>
				),
				extra: (
					<DependantPanelExtra
						personID={personID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsAssociatedPeopleModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			}
		],
		[
			activeKeys,
			associatedPeopleRef,
			associatedTeamsRef,
			permissionsRef,
			credentialsRef,
			isEditingPermissions,
			isEditingDependants,
			isEditingCredentials,
			isEditingTeams,
			marginXS,
			personID,
			togglePanel
		]
	);

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	// Find user type once to avoid repetitive lookups
	const userType = useMemo(
		() => userTypes.find((type) => type.userTypeID === person?.userTypeID)?.name || '-',
		[userTypes, person?.userTypeID]
	);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			closeIcon={false}
			styles={drawerStyles}
			width={871}
		>
			{!person ? (
				<PersonDrawerSkeleton />
			) : (
				<Flex vertical>
					<EditDrawerHeader
						personID={person?.personID}
						displayName={person?.displayName}
						email={person?.email}
						photoUrl={person?.photoUrl}
						fBPersonID={person?.fBPersonID}
					/>
					<div
						style={{
							marginTop: paddingXXL
						}}
					>
						<ManagePersonDropdown personID={personID} />
					</div>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					<Row>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: padding }}>
								<InfoTuple
									icon={<SitesIcon />}
									text='Sites'
									align='start'
									value={<EditSiteSelect personID={personID} />}
								/>

								<InfoTuple
									icon={<ContactIcon />}
									text='Contact'
									value={
										<EditContactInput
											personID={personID}
											error={errorContact}
											setError={setErrorContact}
										/>
									}
									error={errorContact}
									errorContent={<ErrorPersonContact />}
								/>

								<InfoTuple icon={<UserIcon />} text='Category' value={userType} />

								<InfoTuple
									icon={<StatusIcon />}
									text='Status'
									value={person?.isActive ? 'Active' : 'Inactive'}
									highlight={person?.isActive ? 'success' : 'error'}
								/>
							</Flex>
						</Col>
					</Row>

					<Divider style={{ margin: `${paddingMD}px 0px ${paddingMD}px` }} />

					<Collapse
						expandIcon={() => null}
						items={items}
						activeKey={activeKeys}
						onChange={handleCollapseChange}
						ghost
					/>
				</Flex>
			)}
		</Drawer>
	);
};

export default PersonDrawer;
